import { ResourceLinks } from '@/constants/resourceLink';
import { useAuth } from '@/hooks/useAuth';
import { styled, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

const ERROR_PAGE_TITLE = 'Something went wrong :(';
const ERROR_PAGE_DESCRIPTION =
  'We ran into an unexpected error. Please try again or contact support if the issue persists.';
const ERROR_PAGE_BUTTON_TEXT = 'Go to Login Page';
const SUPPORT_TEXT = 'Contact Support';
const SUPPORT_URL = `mailto:${ResourceLinks.supportEmail}`;
const Anchor = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const ErrorBoundaryComponent = () => {
  const theme = useTheme();
  const { logout } = useAuth();
  const handleRedirectToLogin = async () => {
    logout();
  };

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.default,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        px: 3,
        height: '100vh',
      }}
      data-testid="error-boundary"
    >
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
        }}
        data-testid="error-boundary-content"
      >
        <Typography
          variant="h3"
          sx={{ fontWeight: 600, color: theme.palette.text.primary }}
          data-testid="error-boundary-title"
        >
          {ERROR_PAGE_TITLE}
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
          data-testid="error-boundary-description"
        >
          {ERROR_PAGE_DESCRIPTION}
        </Typography>

        <Button
          variant="contained"
          sx={{
            borderRadius: '8px',
            width: 'max-content',
          }}
          onClick={handleRedirectToLogin}
          data-testid="error-boundary-button"
        >
          {ERROR_PAGE_BUTTON_TEXT}
        </Button>

        <Typography
          variant="body2"
          sx={{ mt: 1 }}
          data-testid="error-boundary-support"
        >
          {'Need help? '}
          <Anchor
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="error-boundary-support-link"
          >
            {SUPPORT_TEXT}
          </Anchor>
        </Typography>
      </Box>
    </Box>
  );
};

export default ErrorBoundaryComponent;
